import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

// Paleta compartida de Gastro Kiq (fase "Global Visual Rollout" — extiende el
// lenguaje visual YA aprobado y desplegado en Dashboard/Sidebar al resto de
// la app). Los tokens estructurales (bg/card/border/text/gray) viven en
// variables CSS (ver public/index.html) para que el toggle de tema los
// retina sin tocar JS. Los colores semánticos (verde/ámbar/azul/morado) son
// iguales en ambos temas — son los mismos que ya usa Overview.
//
// `red` es la identidad de marca (vibrant pink #FF2D78, igual que
// overviewTheme.js/Layout.js) — se usa para accents, navegación activa y
// ciertos KPIs, NUNCA para acciones destructivas. Para eso existe `danger`
// (#DC2626), separado a propósito (ver ConfirmDialog). Ya NO existe un
// "rosa secundario" independiente (`pink` apuntaba antes a #E84A72): esa
// identidad separada quedó deprecada — `darkBtnAccent` ahora es un botón
// secundario neutral (blanco/charcoal/border), no relleno de color.
export const dark = {
  bg: 'var(--bg)', card: 'var(--card)', cardAlt: 'var(--card-alt)', cardHover: 'var(--card-hover)',
  modalBg: 'var(--modal-bg)', modalBorder: 'var(--modal-border)',
  border: 'var(--border)', borderSoft: 'var(--border-soft)', text: 'var(--text)', textStrong: 'var(--text-strong)',
  gray: 'var(--gray)', grayDim: 'var(--gray-dim)', grayFaint: 'var(--gray-faint)',
  inputBg: 'var(--input-bg)',
  green: '#22C55E', red: '#FF2D78', amber: '#F59E0B', blue: '#38BDF8', purple: '#A78BFA',
  pink: '#FF2D78', indigo: '#6366F1', danger: '#DC2626',
  accentGrad: 'linear-gradient(180deg, #FF2D78, #B7004F)',
  mono: "'JetBrains Mono', monospace",
  // Finanzas (design_handoff_finanzas) — colores de categoría dedicados
  // (COGS/Labor/Overhead/Beneficio) y la superficie "elevada" de tarjeta de
  // sección/banner KIQ. Deliberadamente separados de green/pink/indigo de
  // arriba (esos ya los consumen otras pantallas; cambiarlos tendría blast
  // radius fuera de Finanzas).
  cogs: 'var(--cogs)', labor: 'var(--labor)', over: 'var(--over)', ben: 'var(--ben)',
  edge: 'var(--edge)', lift: 'var(--lift)', tint: 'var(--tint)', tint2: 'var(--tint2)', track: 'var(--track)',
  negbg: 'var(--negbg)',
  // Objetivos — QA en vivo (2026-09-24): --edge/--lift de Finanzas (arriba)
  // resultaron casi imperceptibles para el marco morado de las 3 cajas
  // grandes de Objetivos (lista/detalle/metas cumplidas) — el propio mockup
  // de Objetivos pedía un valor más vivo. Token separado, exclusivo de
  // Objetivos; Finanzas sigue con edge/lift tal cual, sin tocar.
  edgeObjetivos: 'var(--edge-objetivos)', liftObjetivos: 'var(--lift-objetivos)',
  // KIQ · Centro de Recomendaciones (design_handoff_kiq) — 'save' (kind
  // "ahorro") separado de 'green' (ya usado por otros estados positivos)
  // para poder distinguir ahorro/ganancia en la misma pantalla.
  save: 'var(--save)', data: 'var(--data)',
  // Objetivos (design_handoff_objetivos) — tintas de texto de estado
  // (Cumplido/No cumplido/Sin objetivo), usadas por el badge de estado y la
  // alerta KIQ del detalle.
  okfg: 'var(--okfg)', badfg: 'var(--badfg)', warnfg: 'var(--warnfg)',
};

// rgba(...) rápido a partir de un hex — usado por los tintes de icon badges
// y los fondos "muy sutiles" de acento que pide el sistema visual.
export const tint = (hex, alpha) => {
  const n = parseInt(hex.replace('#', ''), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};

export const darkInput = {
  padding: '10px 12px', border: `1px solid ${dark.border}`, borderRadius: '8px', fontSize: '13.5px',
  outline: 'none', background: dark.inputBg, color: dark.text, width: '100%', boxSizing: 'border-box',
};
export const darkLabel = { fontSize: '11px', fontWeight: '700', letterSpacing: '.06em', color: dark.gray, textTransform: 'uppercase' };
export const darkField = { display: 'flex', flexDirection: 'column', gap: '6px' };
export const darkBtnPrimary = {
  height: '36px', padding: '0 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,.14)', color: '#fff',
  fontSize: '13px', fontWeight: '700', cursor: 'pointer', background: dark.accentGrad,
  boxShadow: '0 1px 0 rgba(255,255,255,.14) inset, 0 4px 14px rgba(255,45,120,.28)', display: 'flex', alignItems: 'center', gap: '7px',
};
export const darkBtnSecondary = {
  height: '36px', padding: '0 14px', borderRadius: '8px', border: `1px solid ${dark.border}`, background: dark.cardAlt,
  color: dark.text, fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px',
};
export const darkBtnGhost = {
  height: '34px', padding: '0 14px', background: dark.cardHover, color: dark.text, border: `1px solid ${dark.border}`,
  borderRadius: '8px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer',
};
// Secondary CON presencia propia — pero YA NO es un botón relleno de rosa
// (esa identidad separada quedó deprecada, ver comentario de `dark` arriba).
// Ahora es neutral (blanco/charcoal) con un borde apenas teñido de marca, así
// tiene algo más de jerarquía que darkBtnSecondary sin competir con el
// primary. No usar para todos los botones.
export const darkBtnAccent = {
  height: '36px', padding: '0 16px', borderRadius: '8px', border: `1px solid rgba(255,45,120,.35)`, color: dark.text,
  fontSize: '13px', fontWeight: '700', cursor: 'pointer', background: dark.card,
  display: 'flex', alignItems: 'center', gap: '7px',
};

// Círculo de color muy suave (8-12% opacity) con un icono encima — el mismo
// tratamiento que ya usan las KPI cards de Overview, reutilizable en
// cualquier pantalla (KPI, alert, empty state, título de sección, etc.).
export function IconBadge({ icon: Icon, color = dark.red, size = 32, iconSize = 16 }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'grid', placeItems: 'center', background: tint(color, 0.12), color,
    }}>
      <Icon size={iconSize} />
    </span>
  );
}

// KPI card compartida. `accent` pinta el borde izquierdo + el IconBadge (el
// mismo lenguaje visual de las KPI cards de Overview); `interactive` agrega
// el hover+zoom pequeño SOLO cuando la card es realmente un acceso/acción
// (p.ej. un KPI que navega a su reporte) — por defecto NO lo tiene, porque
// la mayoría de estos KPIs son puramente informativos.
// `accentSide`: 'left' (default, sin cambios — el borde izquierdo de 3px
// que ya usan Overview y el resto de la app) o 'top' — variante pedida por
// el rediseño de Master Price (design_handoff_master_price): borde
// uniforme + acento de 2px arriba, radio/padding/tipografía de valor
// ligeramente distintos. 'left' queda pixel-idéntico a como estaba: ningún
// consumidor existente de DarkKpi debe notar ningún cambio si no pasa
// `accentSide="top"` explícitamente.
export function DarkKpi({ label, value, valueColor, sub, badge, badgeColor, icon, accent, interactive, onClick, accentSide = 'left' }) {
  const [hover, setHover] = useState(false);
  const zoom = interactive && hover;
  const esTop = accentSide === 'top';
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => interactive && setHover(true)}
      onMouseLeave={() => interactive && setHover(false)}
      style={{
        border: `1px solid ${dark.border}`,
        borderLeft: esTop ? undefined : (accent ? `3px solid ${accent}` : `1px solid ${dark.border}`),
        borderTop: esTop ? `2px solid ${accent || dark.red}` : undefined,
        background: dark.card, borderRadius: esTop ? '15px' : '12px', padding: esTop ? '13px 18px' : '15px 16px',
        display: 'flex', flexDirection: 'column', gap: esTop ? '6px' : '10px',
        cursor: interactive ? 'pointer' : 'default',
        transform: zoom ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: zoom ? '0 12px 28px rgba(15,23,42,0.10)' : 'none',
        transition: 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: esTop ? '10px' : '11px', fontWeight: '700', letterSpacing: esTop ? '.14em' : '.07em', textTransform: esTop ? 'uppercase' : undefined, color: dark.grayDim }}>{label}</span>
        {icon && !badge && <IconBadge icon={icon} color={accent || dark.red} />}
        {badge && <span style={{ fontSize: '11px', fontWeight: '700', color: badgeColor || '#94A3B8', background: (badgeColor || '#94A3B8') + '20', border: `1px solid ${(badgeColor || '#94A3B8')}40`, padding: '2px 6px', borderRadius: '5px' }}>{badge}</span>}
      </div>
      <div style={{ fontSize: esTop ? '26px' : '25px', fontWeight: esTop ? '700' : '800', letterSpacing: '-0.02em', color: valueColor || dark.textStrong, fontFamily: dark.mono, fontVariantNumeric: esTop ? 'tabular-nums' : undefined }}>{value}</div>
      {sub && <div style={{ fontSize: esTop ? '12px' : '11.5px', color: dark.grayDim }}>{sub}</div>}
    </div>
  );
}

// Card base (informativa, sin hover marcado). Para una card que además es un
// acceso clickeable a otra pantalla/acción, usar `interactive` — agrega el
// hover+zoom pequeño; nunca usarlo en cards que solo muestran información.
export function DarkCard({ title, subtitle, action, children, noPad, interactive, onClick, accent }) {
  const [hover, setHover] = useState(false);
  const zoom = interactive && hover;
  return (
    <section
      onClick={onClick}
      onMouseEnter={() => interactive && setHover(true)}
      onMouseLeave={() => interactive && setHover(false)}
      style={{
        border: `1px solid ${dark.border}`, borderLeft: accent ? `3px solid ${accent}` : undefined,
        background: dark.card, borderRadius: '12px', overflow: 'hidden',
        cursor: interactive ? 'pointer' : 'default',
        transform: zoom ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: zoom ? '0 12px 28px rgba(15,23,42,0.10)' : 'none',
        transition: 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease',
      }}
    >
      {(title || action) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', borderBottom: `1px solid ${dark.borderSoft}` }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: dark.text }}>{title}</div>
            {subtitle && <div style={{ fontSize: '11.5px', color: dark.grayDim, marginTop: '2px' }}>{subtitle}</div>}
          </div>
          {action}
        </div>
      )}
      <div style={{ padding: noPad ? 0 : '16px' }}>{children}</div>
    </section>
  );
}

export function DarkModal({ titulo, subtitulo, onCerrar, ancho = '480px', children, footer }) {
  const contenedorRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') onCerrar?.(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onCerrar]);

  // Focus management sin dependencias externas: al abrir, movemos el foco al
  // propio diálogo (tabIndex=-1: foco programático, no entra al orden de Tab);
  // al cerrar, lo devolvemos al elemento que lo abrió. Nota: esto NO es un
  // focus-trap completo — Tab puede seguir saliendo del modal hacia el resto
  // de la página. Implementar un trap robusto sin librería (manejar Shift+Tab,
  // elementos focusables dinámicos, iframes, etc.) es fácil de hacer mal; se
  // deja documentado como limitación conocida en vez de una versión frágil.
  useEffect(() => {
    const elementoPrevio = document.activeElement;
    contenedorRef.current?.focus();
    return () => { elementoPrevio?.focus?.(); };
  }, []);

  return (
    <div onClick={onCerrar} role="presentation" style={{ position: 'fixed', inset: 0, background: 'rgba(8,9,13,.72)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' }}>
      <div ref={contenedorRef} tabIndex={-1} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={titulo} style={{ width: '100%', maxWidth: ancho, border: `1px solid ${dark.modalBorder}`, background: dark.modalBg, borderRadius: '14px', boxShadow: '0 30px 80px rgba(0,0,0,.35)', overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column', outline: 'none' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${dark.border}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{ fontSize: '15px', fontWeight: '800', letterSpacing: '-0.02em', color: dark.textStrong }}>{titulo}</span>
            {subtitulo && <span style={{ fontSize: '12px', color: dark.grayDim }}>{subtitulo}</span>}
          </div>
          {/* Fase 3.4.3 (Defecto F) — el botón X no tenía nombre accesible
              (un ícono solo, sin texto). `titulo` ya es obligatorio y
              específico por cada instancia de DarkModal (p.ej. "Receta con
              KIQ", "Editar receta", "Cambios sin guardar" en ConfirmDialog),
              así que el aria-label describe CADA modal correctamente sin
              necesitar una prop nueva ni tocar ningún caller. */}
          <button onClick={onCerrar} aria-label={`Cerrar ${titulo}`} style={{ width: '28px', height: '28px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: dark.cardHover, border: `1px solid ${dark.border}`, borderRadius: '7px', cursor: 'pointer', color: dark.gray }}><X size={14} /></button>
        </div>
        <div style={{ padding: '20px', overflowY: 'auto' }}>{children}</div>
        {/* Fase 3.4.9 (C2) — footer compartido por TODOS los modales de la
            app. A ancho angosto, un footer con varios botones (p.ej. una
            etiqueta de estado + "Cancelar" + un botón primario de 2
            palabras como "Crear receta") podía no caber en una sola fila;
            sin flex-wrap, el botón primario se encogía por debajo del
            ancho de su propio texto y la etiqueta envolvía a 2 líneas
            dentro del botón (defecto confirmado). flex-wrap permite que
            sobrantes bajen a una nueva línea en vez de aplastar un botón —
            en escritorio (donde todo ya cabe en una fila) esto es un no-op
            byte-idéntico. */}
        {footer && <div style={{ padding: '14px 20px', borderTop: `1px solid ${dark.border}`, background: dark.cardAlt, display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '9px', flexShrink: 0 }}>{footer}</div>}
      </div>
    </div>
  );
}

export function DarkEmpty({ icon, text }) {
  return (
    <div style={{ padding: '50px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', color: dark.grayDim, fontSize: '13px' }}>
      {icon}<span>{text}</span>
    </div>
  );
}

export function DarkTh({ children, align = 'left', width }) {
  return <th style={{ textAlign: align, fontSize: '10.5px', fontWeight: '700', letterSpacing: '.07em', color: dark.grayDim, padding: '10px 16px', borderBottom: `1px solid ${dark.borderSoft}`, background: dark.cardAlt, width }}>{children}</th>;
}
export function DarkTd({ children, align = 'left', mono, strong, color, width }) {
  return <td style={{ textAlign: align, padding: '12px 16px', fontSize: '13px', fontWeight: strong ? '600' : '400', color: color || dark.text, fontFamily: mono ? dark.mono : undefined, width }}>{children}</td>;
}
